import * as Sentry from '@sentry/react'

/**
 * Explicit reporting for failures we *catch* and turn into a user-facing message.
 *
 * Sentry's automatic instrumentation only sees what reaches the global handlers or an ErrorBoundary.
 * A `catch` that shows a toast is invisible to it — which is how a Krona chart that never finished
 * mounting could be reported by a customer without a single Sentry event to match it against.
 *
 * Use for dead-end failures (the feature is now unusable), not for retried or expected errors.
 */

/** `order-download` breaks the "not for expected errors" rule on purpose: the trickle is the signal. */
export type HandledErrorFeature = 'krona' | 'order-download'

/**
 * Report a caught, user-visible failure. `feature` becomes a Sentry tag so these can be alerted on
 * per feature; `context` is attached as the `handled_failure` context for debugging.
 * Never throws — reporting must not compound the original failure.
 */
export const reportHandledError = (
  feature: HandledErrorFeature,
  error: unknown,
  context: Record<string, string | number | boolean | undefined> = {}
): void => {
  try {
    Sentry.captureException(error, {
      tags: { feature, handled: true },
      contexts: { handled_failure: { ...context } }
    })
  } catch {
    /* error reporting must never throw into a user action */
  }
}
